// PATH: src/components/activation/useActivation.tsx
/**
 * Ouvrir / fermer un cours ou une matière aux élèves (demande du dirigeant du
 * 13/10/2026). Deux interrupteurs indépendants : un cours n'est servi que si
 * le sien ET celui de sa matière sont ouverts. Fermer une matière ne touche
 * pas l'interrupteur de ses cours : la rouvrir les rend tels qu'ils étaient.
 *
 * Rien n'est supprimé (objectifs, faits, progression, quêtes) : la
 * confirmation le dit, parce qu'on ferme pour préparer, pas pour jeter.
 */
import * as React from "react";
import { useDocumentsApi, useSubjectsApi } from "../../api";
import type { DocumentActivation, Subject, SubjectActivation } from "../../models/domain";
import { errorMessage, useConfirm, useToast } from "../ui";
import { KEPT, subjectConfirmMessage } from "./activationText";

export const useActivation = () => {
  const { getSubjectActivation, setSubjectActivation } = useSubjectsApi();
  const { setDocumentActivation } = useDocumentsApi();
  const confirm = useConfirm();
  const toast = useToast();

  /** Rend le nouvel état, ou null si annulé / en échec (le toast l'a dit). */
  const toggleSubject = React.useCallback(
    async (subject: Pick<Subject, "id" | "name">, next: boolean): Promise<SubjectActivation | null> => {
      try {
        const { data: current } = await getSubjectActivation(subject.id);
        const ok = await confirm({
          title: next ? "Ouvrir la matière aux élèves ?" : "Fermer la matière aux élèves ?",
          message: subjectConfirmMessage(subject.name, next, current),
          confirmLabel: next ? "Ouvrir" : "Fermer",
          danger: !next,
        });
        if (!ok) return null;
        const { data } = await setSubjectActivation(subject.id, next);
        toast.success(next ? `« ${subject.name} » est ouverte aux élèves.` : `« ${subject.name} » est fermée aux élèves.`);
        return data;
      } catch (e) {
        toast.error(errorMessage(e, "Échec du changement d'ouverture de la matière."));
        return null;
      }
    },
    [getSubjectActivation, setSubjectActivation, confirm, toast],
  );

  const toggleDocument = React.useCallback(
    async (
      doc: { id: string; title: string; subject_active?: boolean },
      next: boolean,
    ): Promise<DocumentActivation | null> => {
      const subjectClosed = doc.subject_active === false;
      const ok = await confirm({
        title: next ? "Ouvrir le cours aux élèves ?" : "Fermer le cours aux élèves ?",
        message: next
          ? `« ${doc.title} » sera proposé aux élèves` +
            (subjectClosed ? " dès que sa matière sera rouverte (elle est fermée)." : ".")
          : `« ${doc.title} » disparaît pour les élèves : plus de catalogue, de parcours ni de nouvelle quête. ${KEPT}`,
        confirmLabel: next ? "Ouvrir" : "Fermer",
        danger: !next,
      });
      if (!ok) return null;
      try {
        const { data } = await setDocumentActivation(doc.id, next);
        toast.success(next ? "Cours ouvert aux élèves." : "Cours fermé aux élèves.");
        return data;
      } catch (e) {
        toast.error(errorMessage(e, "Échec du changement d'ouverture du cours."));
        return null;
      }
    },
    [setDocumentActivation, confirm, toast],
  );

  return { toggleSubject, toggleDocument };
};
